import { SPEED_TO_LEAD_METRICS_API_PATH, SPEED_TO_LEAD_TOUCHPOINTS_API_PATH } from 'components/SpeedToLead/constants';
import { AlertPricingType } from 'types/alerts';
import { PaginatedResponseType } from 'types/common';
import {
  AICompetitiveIntelligenceRankingRow,
  AICompetitiveIntelligenceType,
  CommunityActivitySummary,
  CompanyAICIStats,
  CompanyAICompetitiveIntelligenceRow,
  CompanySpeedToLead,
  MarketPositionTableType,
  MyCommunitiesMarketRateComparison,
  MyCommunitiesQuickFilterType,
  MyCommunitiesTable,
  RecommendationDetailsType,
  RecommendationType,
  RecommendationWebEvidenceComparisonType,
  SalesPlaybookType,
  SpeedToLeadMetrics,
  SpeedToLeadTouchpointPreviews,
} from 'types/recommendations';

import { QueryParamsType } from './types';
import axios from '../config/axiosPrivate';

const RECOMMENDATION_WEB_EVIDENCE_API_PATH = (recommendationId: number, communityId: number) =>
  `/recommendations/${recommendationId}/communities/${communityId}/web-evidence/`;

export const getRecommendations = async (
  companyId: number,
  filters: QueryParamsType
): Promise<RecommendationType[]> => {
  const response = await axios.get(`/recommendations/?company_id=${companyId}`, { params: filters });
  return response.data;
};

export const getRecommendationDetails = async (id: number): Promise<RecommendationDetailsType> => {
  const response = await axios.get(`/recommendations/${id}`);
  return response.data;
};

export const getRecommendationWebEvidence = async (
  recommendationId: number,
  communityId: number
): Promise<RecommendationWebEvidenceComparisonType> => {
  const response = await axios.get(RECOMMENDATION_WEB_EVIDENCE_API_PATH(recommendationId, communityId));
  return response.data;
};

export const getAICompetitiveIntelligence = async (communityId: number): Promise<AICompetitiveIntelligenceType> => {
  const response = await axios.get(`/recommendations/ai-competitive-intelligence/${communityId}/`);
  return response.data;
};

export const getCompanyAICompetitiveIntelligence = async (
  companyId: number,
  filters: QueryParamsType
): Promise<PaginatedResponseType<CompanyAICompetitiveIntelligenceRow>> => {
  const response = await axios.get(`/recommendations/ai-competitive-intelligence/company/`, {
    params: { company_id: companyId, ...filters },
  });
  return response.data;
};

export const getCompanyAICICommunityRankings = async (
  companyId: number
): Promise<CompanyAICompetitiveIntelligenceRow[]> => {
  const response = await axios.get(`/recommendations/ai-competitive-intelligence/company/`, {
    params: { company_id: companyId },
  });
  return response.data;
};

export const getCompanyAICIStats = async (companyId: number): Promise<CompanyAICIStats> => {
  const response = await axios.get(`/recommendations/ai-competitive-intelligence/company/stats/`, {
    params: { company_id: companyId },
  });
  return response.data;
};

export type GetMyCommunitiesTableParams = {
  community_ids?: number[];
  quick_filter?: MyCommunitiesQuickFilterType;
  time_period_days?: number;
  rate_gap_threshold_percent?: number;
  search?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
};

export const getMyCommunitiesTable = async (
  companyId: number,
  params: GetMyCommunitiesTableParams
): Promise<MyCommunitiesTable> => {
  const response = await axios.get(`/recommendations/my-communities/company/`, {
    params: { company_id: companyId, ...params },
    paramsSerializer: {
      indexes: null,
    },
  });
  return response.data;
};

export const getMyCommunitiesMarketRate = async (communityId: number): Promise<MyCommunitiesMarketRateComparison[]> => {
  const response = await axios.get(`/recommendations/my-communities/${communityId}/market-rate/`);
  return response.data;
};

export const getCompanySpeedToLead = async (companyId: number): Promise<CompanySpeedToLead> => {
  const response = await axios.get(`/recommendations/speed-to-lead/company/`, {
    params: { company_id: companyId },
  });
  return response.data;
};

export const getAICategoryRankings = async (
  communityId: number,
  category: string
): Promise<{ rows: AICompetitiveIntelligenceRankingRow[] }> => {
  const response = await axios.get(`/recommendations/ai-competitive-intelligence/${communityId}/rankings/${category}/`);
  return response.data;
};

export const getSalesPlaybook = async (communityId: number): Promise<SalesPlaybookType> => {
  const response = await axios.get(`/recommendations/ai-competitive-intelligence-sales-playbook/${communityId}/`);
  return response.data;
};

export const getSpeedToLeadMetrics = async (communityId: number, companyId: number): Promise<SpeedToLeadMetrics> => {
  const response = await axios.get(SPEED_TO_LEAD_METRICS_API_PATH(communityId), {
    params: { company_id: companyId },
  });
  return response.data;
};

export const getSpeedToLeadTouchpointPreviews = async (
  communityId: number,
  sourceCommunityId: number,
  companyId: number,
  analysisRunId: number
): Promise<SpeedToLeadTouchpointPreviews> => {
  const response = await axios.get(SPEED_TO_LEAD_TOUCHPOINTS_API_PATH(communityId, sourceCommunityId), {
    params: { company_id: companyId, analysis_run_id: analysisRunId },
  });
  return response.data;
};

export const getCommunityActivitySummary = async (communityId: number): Promise<CommunityActivitySummary> => {
  const response = await axios.get(`/recommendations/community-activity-summary/${communityId}/`);
  return response.data;
};

export const getMarketPositionTable = async (communityId: number): Promise<MarketPositionTableType> => {
  const response = await axios.get(
    `/recommendations/ai-competitive-intelligence/${communityId}/market-position-table/`
  );
  return response.data;
};

export const regenerateAICompetitiveIntelligence = async (payload: {
  community: number;
  category_config_ids: number[];
  action_plans: boolean;
  sales_talk_tracks: boolean;
}) => {
  const response = await axios.post('/recommendations/ai-competitive-intelligence/regenerate/', payload);
  return response.data;
};
